import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, Plus, Save, Users, Layers, Edit, X, RefreshCw, CheckCircle, AlertTriangle, AlertCircle, HelpCircle } from 'lucide-react';

// Ortak Input Stili
const inputClasses = "w-full p-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition";

// --- ALT BİLEŞEN: Bildirim Modalı (Başarılı/Hata) ---
const StatusModal = ({ isOpen, type, message, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl p-6 max-w-sm w-full transform transition-all scale-100">
        <div className="flex flex-col items-center text-center">
          <div className={`p-3 rounded-full mb-4 ${type === 'success' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
            {type === 'success' ? <CheckCircle size={40} /> : <AlertCircle size={40} />}
          </div>
          <h3 className="text-xl font-bold text-white mb-2">
            {type === 'success' ? 'İşlem Başarılı!' : 'Bir Sorun Oluştu'}
          </h3>
          <p className="text-gray-400 mb-6">{message}</p>
          <button 
            onClick={onClose}
            className={`w-full py-2.5 rounded-xl font-semibold text-white transition shadow-lg
              ${type === 'success' ? 'bg-green-600 hover:bg-green-700 shadow-green-900/20' : 'bg-red-600 hover:bg-red-700 shadow-red-900/20'}`}
          >
            Tamam
          </button>
        </div>
      </div>
    </div>
  );
};

// --- ALT BİLEŞEN: Silme Onay Modalı ---
const DeleteModal = ({ isOpen, title, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl p-6 max-w-sm w-full">
        <div className="flex flex-col items-center text-center">
          <div className="p-3 rounded-full mb-4 bg-orange-900/30 text-orange-400">
            <AlertTriangle size={40} />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Emin misiniz?</h3>
          <p className="text-gray-400 mb-6">
            <span className="font-semibold text-gray-200">"{title}"</span> başlıklı quizi ve tüm sorularını silmek üzeresiniz. Bu işlem geri alınamaz.
          </p>
          <div className="flex gap-3 w-full">
            <button 
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl font-semibold text-gray-300 bg-gray-700 hover:bg-gray-600 transition"
            >
              Vazgeç
            </button>
            <button 
              onClick={onConfirm}
              className="flex-1 py-2.5 rounded-xl font-semibold text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-900/20 transition"
            >
              Evet, Sil
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- ANA SAYFA BİLEŞENİ ---
const AdminQuizPage = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  // Modal State'leri
  const [statusModal, setStatusModal] = useState({ open: false, type: 'success', message: '' });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, title: '' });

  const initialFormState = {
    title: '',
    description: '',
    image_url: '',
    questions: [
      {
        question_text: '',
        answers: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }]
      }
    ]
  };

  const [newQuiz, setNewQuiz] = useState(initialFormState);
  
  const API_URL = process.env.REACT_APP_API_URL; 

  const fetchQuizzes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/quizzes`);
      setQuizzes(res.data);
      setLoading(false);
    } catch (error) {
      console.error("Quizler yüklenirken hata:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizzes();
  }, []);

  // --- Form Handlers ---

  const handleQuizChange = (e) => {
    setNewQuiz({ ...newQuiz, [e.target.name]: e.target.value });
  };

  const handleQuestionChange = (qIndex, val) => {
    const updatedQuestions = [...newQuiz.questions];
    updatedQuestions[qIndex].question_text = val;
    setNewQuiz({ ...newQuiz, questions: updatedQuestions });
  };

  const handleAnswerChange = (qIndex, aIndex, field, val) => {
    const updatedQuestions = [...newQuiz.questions];
    updatedQuestions[qIndex].answers[aIndex][field] = val;
    setNewQuiz({ ...newQuiz, questions: updatedQuestions });
  };

  const addQuestion = () => {
    setNewQuiz({
      ...newQuiz,
      questions: [
        ...newQuiz.questions,
        { question_text: '', answers: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }] }
      ]
    });
  };

  const addAnswerOption = (qIndex) => {
    const updatedQuestions = [...newQuiz.questions];
    updatedQuestions[qIndex].answers.push({ text: '', isCorrect: false });
    setNewQuiz({ ...newQuiz, questions: updatedQuestions });
  };

  const removeQuestion = (qIndex) => {
    const updatedQuestions = newQuiz.questions.filter((_, index) => index !== qIndex);
    setNewQuiz({ ...newQuiz, questions: updatedQuestions });
  };

  const resetForm = () => {
    setNewQuiz(initialFormState);
    setEditingId(null);
  };

  // --- Modal Yardımcıları ---
  const showStatus = (type, message) => {
    setStatusModal({ open: true, type, message });
  };

  // --- API İşlemleri ---

  const confirmDelete = (id, title) => {
    setDeleteModal({ open: true, id, title });
  };

  const performDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/admin/quizzes/${deleteModal.id}`);
      fetchQuizzes();
      if (editingId === deleteModal.id) resetForm();
      setDeleteModal({ ...deleteModal, open: false });
      showStatus('success', 'Quiz başarıyla silindi.');
    } catch (error) {
      setDeleteModal({ ...deleteModal, open: false });
      showStatus('error', 'Silme işlemi sırasında hata oluştu.');
      console.error(error);
    }
  };

  const handleEditClick = async (quizId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/quizzes/${quizId}`);
      const data = res.data;

      setNewQuiz({
        title: data.title,
        description: data.description,
        image_url: data.image_url || '',
        questions: data.questions.map(q => ({
          question_text: q.question_text,
          answers: Array.isArray(q.answers) ? q.answers.map(a => ({
             text: a.text || a.option || "", 
             isCorrect: (a.isCorrect === true) || (a.is_correct === true)
          })) : [
            { text: '', isCorrect: false }, 
            { text: '', isCorrect: false }
          ]
        }))
      });
      
      setEditingId(quizId);
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error("Quiz detayları alınamadı:", error);
      showStatus('error', 'Quiz detayları yüklenemedi.');
    }
  };

  const handleSubmit = async () => {
    if (!newQuiz.title || newQuiz.questions.length === 0) {
      showStatus('error', 'Lütfen başlık ve en az bir soru giriniz.');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/quizzes/${editingId}`, newQuiz);
        showStatus('success', 'Quiz güncellendi!');
      } else {
        await axios.post(`${API_URL}/api/admin/quizzes/create`, newQuiz);
        showStatus('success', 'Yeni quiz oluşturuldu!');
      }

      fetchQuizzes();
      resetForm();

    } catch (error) {
      console.error("İşlem hatası:", error);
      showStatus('error', 'Kaydetme işlemi başarısız oldu.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6 md:p-12 font-sans">
      
      {/* --- MODALLAR --- */}
      <StatusModal 
        isOpen={statusModal.open} 
        type={statusModal.type} 
        message={statusModal.message} 
        onClose={() => setStatusModal({ ...statusModal, open: false })}
      />
      
      <DeleteModal 
        isOpen={deleteModal.open}
        title={deleteModal.title}
        onClose={() => setDeleteModal({ ...deleteModal, open: false })}
        onConfirm={performDelete}
      />

      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
            <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-indigo-500">
                Quiz Yönetimi
            </h1>
            <p className="text-gray-400 mt-1">
                Kullanıcılar için etkileşimli testler oluşturun ve yönetin.
            </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
          {/* SOL KOLON: Liste */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-gray-800 p-6 rounded-2xl border border-gray-700 shadow-xl sticky top-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-indigo-400">
                <Layers size={20} />
                Mevcut Quizler
              </h2>
              
              {loading ? (
                <div className="text-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
                    <p className="text-gray-500 mt-2 text-sm">Yükleniyor...</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
                  {quizzes.map((quiz) => (
                    <div 
                      key={quiz.id} 
                      className={`p-4 border rounded-xl transition group 
                        ${editingId === quiz.id 
                            ? 'border-indigo-500 bg-indigo-900/20' 
                            : 'bg-gray-900/50 border-gray-700 hover:border-gray-600 hover:bg-gray-800'
                        }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-gray-200 leading-tight pr-2">{quiz.title}</h3>
                        
                        <div className="flex gap-1 shrink-0">
                          <button 
                            onClick={() => handleEditClick(quiz.id)}
                            className="p-1.5 text-gray-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded transition"
                            title="Düzenle"
                          >
                            <Edit size={16} />
                          </button>
                          <button 
                            onClick={() => confirmDelete(quiz.id, quiz.title)}
                            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition"
                            title="Sil"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-500 line-clamp-2 mb-3">{quiz.description}</p>
                      
                      <span className="inline-flex items-center gap-1 text-xs font-medium bg-gray-700 text-gray-300 px-2 py-1 rounded-md">
                        <Users size={12} />
                        {quiz.participant_count} Katılımcı
                      </span>
                    </div>
                  ))}
                  
                  {quizzes.length === 0 && (
                    <div className="text-center py-8 bg-gray-900/50 rounded-xl border border-dashed border-gray-700">
                        <p className="text-sm text-gray-500">Henüz hiç quiz eklenmemiş.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* SAĞ KOLON: Form */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800 p-6 rounded-2xl border border-gray-700 shadow-xl">
              <div className="flex justify-between items-center mb-6 border-b border-gray-700 pb-4">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  {editingId ? (
                     <>
                       <div className="p-2 bg-orange-500/20 rounded-lg">
                           <RefreshCw size={20} className="text-orange-500"/> 
                       </div>
                       Quiz Düzenle
                     </>
                  ) : (
                     <>
                       <div className="p-2 bg-indigo-500/20 rounded-lg">
                           <Plus size={20} className="text-indigo-400"/>
                       </div>
                       Yeni Quiz Oluştur
                     </>
                  )}
                </h2>

                {editingId && (
                  <button 
                    onClick={resetForm}
                    className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600"
                  >
                    <X size={16} /> Vazgeç
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 gap-4 mb-6">
                <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1 ml-1">Başlık</label>
                    <input
                    type="text"
                    name="title"
                    placeholder="Örn: Osmanlı Mimarisi Bilgi Yarışması"
                    value={newQuiz.title}
                    onChange={handleQuizChange}
                    className={inputClasses}
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1 ml-1">Açıklama</label>
                    <textarea
                    name="description"
                    placeholder="Quiz hakkında kısa bilgi..."
                    value={newQuiz.description}
                    onChange={handleQuizChange}
                    className={inputClasses}
                    rows="2"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1 ml-1">Kapak Görseli URL</label>
                    <input
                    type="text"
                    name="image_url"
                    placeholder="https://..."
                    value={newQuiz.image_url}
                    onChange={handleQuizChange}
                    className={inputClasses}
                    />
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4 mt-8">
                  <HelpCircle className="text-indigo-400" size={20} />
                  <h3 className="text-lg font-bold text-white">Sorular</h3>
              </div>

              <div className="space-y-6">
                {newQuiz.questions.map((q, qIndex) => (
                  <div key={qIndex} className="bg-gray-900/50 p-5 rounded-xl border border-gray-700 relative group transition-all hover:border-indigo-500/50">
                    
                    {newQuiz.questions.length > 1 && (
                      <button 
                        onClick={() => removeQuestion(qIndex)}
                        className="absolute top-4 right-4 text-gray-500 hover:text-red-400 transition"
                        title="Soruyu Sil"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}

                    <h4 className="text-xs font-bold text-indigo-400 uppercase mb-2 tracking-wide">Soru {qIndex + 1}</h4>
                    <input
                      type="text"
                      placeholder="Soru metnini giriniz..."
                      value={q.question_text}
                      onChange={(e) => handleQuestionChange(qIndex, e.target.value)}
                      className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white mb-4 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                    />

                    <div className="space-y-2 pl-4 border-l-2 border-gray-700">
                      {q.answers.map((answer, aIndex) => (
                        <div key={aIndex} className="flex items-center gap-3">
                          <div 
                            onClick={() => {
                                const updatedQuestions = [...newQuiz.questions];
                                updatedQuestions[qIndex].answers.forEach(a => a.isCorrect = false);
                                updatedQuestions[qIndex].answers[aIndex].isCorrect = true;
                                setNewQuiz({ ...newQuiz, questions: updatedQuestions });
                            }}
                            className={`w-5 h-5 rounded-full border flex items-center justify-center cursor-pointer transition
                                ${answer.isCorrect ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-gray-500 hover:border-gray-400'}`}
                          >
                              {answer.isCorrect && <div className="w-2 h-2 bg-white rounded-full"></div>}
                          </div>
                          
                          <input
                            type="text"
                            placeholder={`Seçenek ${aIndex + 1}`}
                            value={answer.text}
                            onChange={(e) => handleAnswerChange(qIndex, aIndex, 'text', e.target.value)}
                            className={`flex-1 p-2 bg-gray-800 border rounded text-sm outline-none transition text-gray-200
                              ${answer.isCorrect ? 'border-indigo-500/50 bg-indigo-900/10' : 'border-gray-600 focus:border-gray-500'}`}
                          />
                        </div>
                      ))}
                      <button
                        onClick={() => addAnswerOption(qIndex)}
                        className="text-xs text-indigo-400 hover:text-indigo-300 hover:underline mt-2 font-medium flex items-center gap-1 transition"
                      >
                        <Plus size={12}/> Seçenek Ekle
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={addQuestion}
                  className="flex items-center justify-center gap-2 px-6 py-3 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-700 transition font-medium"
                >
                  <Plus size={18} /> Yeni Soru
                </button>
                
                <button
                  onClick={handleSubmit}
                  className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-white rounded-xl shadow-lg transition font-bold transform hover:-translate-y-0.5
                    ${editingId 
                      ? 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 shadow-orange-900/20' 
                      : 'bg-gradient-to-r from-indigo-600 to-teal-500 hover:from-indigo-700 hover:to-teal-600 shadow-indigo-900/20'}`}
                >
                  <Save size={18} /> 
                  {editingId ? 'Değişiklikleri Güncelle' : 'Quizi Kaydet'}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminQuizPage;